import * as React from 'react';
import { CircleNotch, Prohibit, XCircle } from '@phosphor-icons/react';
import { Button, Card, Stack, Text } from '@fragments-sdk/ui';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import { useOrgContext } from '@/hooks/useOrgContext';
import s from './governance.module.scss';

type Waiver = {
  _id: Id<'suppressions'>;
  carrier: 'in-source' | 'cloud-waiver';
  status: 'active' | 'revoked';
  scope: 'fingerprint' | 'rule' | 'path' | 'rule-path';
  fingerprint?: string;
  ruleId?: string;
  filePath?: string;
  source?: 'ci' | 'repo-scan' | 'manual';
  reason?: string;
  createdBy?: string;
  expiresAt?: number;
  createdAt: number;
  revokedAt?: number;
};

type SuppressionState = 'active' | 'expiring' | 'expired' | 'revoked' | 'all';
type SuppressionCarrier = 'all' | 'in-source' | 'cloud-waiver';

const STATE_FILTERS: Array<{ label: string; value: SuppressionState }> = [
  { label: 'Active', value: 'active' },
  { label: 'Expiring', value: 'expiring' },
  { label: 'Expired', value: 'expired' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'All', value: 'all' },
];

const CARRIER_FILTERS: Array<{ label: string; value: SuppressionCarrier }> = [
  { label: 'All carriers', value: 'all' },
  { label: 'In-source', value: 'in-source' },
  { label: 'Cloud', value: 'cloud-waiver' },
];

interface GovernanceWaiversProps {
  projectId: Id<'projects'>;
  title?: string;
  description?: string;
  showEmptyState?: boolean;
}

export function GovernanceWaivers({
  projectId,
  title = 'Active waivers',
  description,
  showEmptyState = false,
}: GovernanceWaiversProps) {
  const { user } = useClerkUser();
  const { activeRole } = useOrgContext();
  const canRevoke = activeRole === 'owner' || activeRole === 'admin';
  const [stateFilter, setStateFilter] =
    React.useState<SuppressionState>('active');
  const [carrierFilter, setCarrierFilter] =
    React.useState<SuppressionCarrier>('all');
  const waivers = useQuery(api.findingsWaivers.listByProject, {
    projectId,
    clerkUserId: user?.id,
    state: stateFilter,
    carrier: carrierFilter === 'all' ? undefined : carrierFilter,
  });
  const revoke = useMutation(api.findingsWaivers.revoke);
  const [revoking, setRevoking] = React.useState<string | null>(null);

  if (waivers === undefined) {
    return (
      <section className={s.waiverSection}>
        <Stack gap="md">
          <Text
            as="div"
            size="sm"
            color="tertiary"
            className={s.waiverLoading}
          >
            <CircleNotch size={14} className={s.spinIcon} />
            Loading waivers
          </Text>
        </Stack>
      </section>
    );
  }

  if (waivers.length === 0) {
    if (!showEmptyState) return null;
    return (
      <section className={s.waiverSection}>
        <Stack gap="md">
          <WaiverHeader
            title={title}
            description={description ?? 'No active suppressions'}
            stateFilter={stateFilter}
            carrierFilter={carrierFilter}
            onStateFilter={setStateFilter}
            onCarrierFilter={setCarrierFilter}
          />
          <Text as="div" size="sm" color="tertiary" className={s.waiverEmpty}>
            <Prohibit size={18} />
            <Text as="span" size="sm" color="tertiary">
              No active suppressions for this repository.
            </Text>
          </Text>
        </Stack>
      </section>
    );
  }

  async function handleRevoke(waiverId: Id<'suppressions'>) {
    setRevoking(waiverId);
    try {
      await revoke({ waiverId });
    } finally {
      setRevoking((current) => (current === waiverId ? null : current));
    }
  }

  return (
    <section className={s.waiverSection}>
      <Stack gap="md">
        <WaiverHeader
          title={title}
          description={
            description ??
            `${waivers.length} suppressed ${
              waivers.length === 1 ? 'finding' : 'findings'
            }`
          }
          stateFilter={stateFilter}
          carrierFilter={carrierFilter}
          onStateFilter={setStateFilter}
          onCarrierFilter={setCarrierFilter}
        />

        <Stack className={s.waiverRows} gap="sm">
          {waivers.map((waiver) => (
            <Card
              key={waiver._id}
              as="article"
              variant="outlined"
              padding="none"
              className={s.waiverRow}
            >
              <Stack className={s.waiverMeta} gap="xs">
                <Text as="span" size="2xs" color="tertiary">
                  {formatScope(waiver)}
                </Text>
                <Text
                  as="span"
                  size="sm"
                  weight="medium"
                  className={s.waiverTarget}
                >
                  {formatTarget(waiver)}
                </Text>
              </Stack>
              <Stack direction="row" wrap gap="sm" className={s.waiverSubmeta}>
                <Text as="span" size="2xs" color="tertiary">
                  {formatCarrier(waiver.carrier)}
                </Text>
                <Text as="span" size="2xs" color="tertiary">
                  {formatCreatedAt(waiver.createdAt)}
                </Text>
                {waiver.source && (
                  <Text as="span" size="2xs" color="tertiary">
                    {formatSource(waiver.source)}
                  </Text>
                )}
                {waiver.expiresAt && (
                  <Text as="span" size="2xs" color="tertiary">
                    Expires {formatCreatedAt(waiver.expiresAt)}
                  </Text>
                )}
                {waiver.revokedAt && (
                  <Text as="span" size="2xs" color="tertiary">
                    Revoked {formatCreatedAt(waiver.revokedAt)}
                  </Text>
                )}
              </Stack>
              {waiver.status === 'active' && canRevoke ? (
                <Button
                  type="button"
                  variant="outlined"
                  size="xs"
                  className={s.waiverRevoke}
                  disabled={revoking === waiver._id}
                  onClick={() => void handleRevoke(waiver._id)}
                >
                  {revoking === waiver._id ? (
                    <CircleNotch size={14} className={s.spinIcon} />
                  ) : (
                    <XCircle size={14} />
                  )}
                  Revoke
                </Button>
              ) : waiver.status === 'active' ? (
                <Text
                  as="span"
                  size="xs"
                  color="tertiary"
                  className={s.waiverStatus}
                >
                  Admin required
                </Text>
              ) : (
                <Text
                  as="span"
                  size="xs"
                  color="tertiary"
                  className={s.waiverStatus}
                >
                  Revoked
                </Text>
              )}
            </Card>
          ))}
        </Stack>
      </Stack>
    </section>
  );
}

function WaiverHeader({
  title,
  description,
  stateFilter,
  carrierFilter,
  onStateFilter,
  onCarrierFilter,
}: {
  title: string;
  description?: string;
  stateFilter: SuppressionState;
  carrierFilter: SuppressionCarrier;
  onStateFilter: (state: SuppressionState) => void;
  onCarrierFilter: (carrier: SuppressionCarrier) => void;
}) {
  return (
    <Stack
      direction={{ base: 'column', md: 'row' }}
      justify="between"
      gap="md"
      className={s.waiverHeader}
    >
      <Stack gap="xs">
        <Text
          as="h2"
          size="sm"
          weight="semibold"
          className={s.waiverTitle}
        >
          <Prohibit size={16} />
          {title}
        </Text>
        {description && (
          <Text
            as="p"
            size="xs"
            color="secondary"
            className={s.waiverDesc}
          >
            {description}
          </Text>
        )}
      </Stack>
      <Stack direction="row" wrap gap="sm" className={s.waiverControls}>
        <div className={s.waiverFilterGroup} aria-label="Suppression status">
          {STATE_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              type="button"
              variant={stateFilter === filter.value ? 'secondary' : 'ghost'}
              size="xs"
              className={s.waiverFilterButton}
              data-active={stateFilter === filter.value || undefined}
              onClick={() => onStateFilter(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <div className={s.waiverFilterGroup} aria-label="Suppression carrier">
          {CARRIER_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              type="button"
              variant={carrierFilter === filter.value ? 'secondary' : 'ghost'}
              size="xs"
              className={s.waiverFilterButton}
              data-active={carrierFilter === filter.value || undefined}
              onClick={() => onCarrierFilter(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </Stack>
    </Stack>
  );
}

function formatScope(waiver: Waiver) {
  if (waiver.scope === 'rule-path') return 'Rule and path';
  return waiver.scope[0].toUpperCase() + waiver.scope.slice(1);
}

function formatTarget(waiver: Waiver) {
  if (waiver.scope === 'fingerprint') {
    return waiver.ruleId ?? waiver.fingerprint ?? 'Fingerprint waiver';
  }
  if (waiver.scope === 'rule') return waiver.ruleId ?? 'Rule waiver';
  if (waiver.scope === 'path') return waiver.filePath ?? 'Path waiver';
  return [waiver.ruleId, waiver.filePath].filter(Boolean).join(' in ');
}

function formatSource(source: NonNullable<Waiver['source']>) {
  if (source === 'ci') return 'CI';
  if (source === 'repo-scan') return 'Repo scan';
  return 'Manual';
}

function formatCarrier(carrier: Waiver['carrier']) {
  return carrier === 'in-source' ? 'In-source' : 'Cloud waiver';
}

function formatCreatedAt(value: number) {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
